import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { toast } from "sonner";
import { Plus, X, Copy, RefreshCw, Trash2 } from "lucide-react";

export default function Team() {
  const { user } = useAuth();
  const [admins, setAdmins] = useState(null);
  const [showInvite, setShowInvite] = useState(false);
  const [invName, setInvName] = useState("");
  const [invEmail, setInvEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [inviteLink, setInviteLink] = useState(""); // shown once after invite/resend

  const load = async () => {
    try {
      const { data } = await api.get("/admin/team");
      setAdmins(data.admins || []);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  useEffect(() => { load(); }, []);

  const invite = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/admin/team/invite", {
        name: invName.trim(), email: invEmail.trim(),
      });
      setInviteLink(data.invite_url || "");
      setShowInvite(false);
      setInvName("");
      setInvEmail("");
      toast.success("Invite sent by email");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const resend = async (a) => {
    try {
      const { data } = await api.post(`/admin/team/${a.id}/resend`);
      setInviteLink(data.invite_url || "");
      toast.success(`Invite resent to ${a.email}`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const remove = async (a) => {
    const verb = a.status === "pending" ? "cancel the invite for" : "remove";
    if (!window.confirm(`Really ${verb} ${a.name || a.email}?`)) return;
    try {
      await api.delete(`/admin/team/${a.id}`);
      toast.success(a.status === "pending" ? "Invite cancelled" : "Admin removed");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(inviteLink);
      toast.success("Invite link copied");
    } catch {
      toast.error("Copy failed — select and copy it manually");
    }
  };

  return (
    <div className="p-8" data-testid="team-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="btw-overline mb-1">Admin</p>
          <h1 className="text-2xl font-bold text-black">Team</h1>
          <p className="text-sm text-black mt-1">Every admin has the same, full access.</p>
        </div>
        <button onClick={() => setShowInvite(true)} data-testid="invite-admin-button" className="btw-btn text-xs">
          <Plus size={14} /> Invite admin
        </button>
      </div>

      {inviteLink && (
        <div className="bg-white border border-[#c19872] rounded-xl p-4 mb-6 flex flex-wrap items-center gap-3" data-testid="invite-link-box">
          <p className="text-xs text-black flex-1 min-w-0 break-all">
            <strong>Invite link (shown once):</strong> {inviteLink}
          </p>
          <button onClick={copy} data-testid="copy-invite-link" className="btw-btn-outline text-xs">
            <Copy size={13} /> Copy invite link
          </button>
          <button onClick={() => setInviteLink("")} className="text-black hover:text-[#b35c5c]" data-testid="dismiss-invite-link">
            <X size={15} />
          </button>
        </div>
      )}

      <div className="bg-white border border-[#c19872] rounded-xl overflow-hidden">
        <table className="w-full text-sm" data-testid="team-table">
          <thead>
            <tr className="border-b border-[#dcd3c6] text-left">
              <th className="px-4 py-3 uppercase text-xs">Name</th>
              <th className="px-4 py-3 uppercase text-xs">Email</th>
              <th className="px-4 py-3 uppercase text-xs">Last login</th>
              <th className="px-4 py-3 uppercase text-xs">Status</th>
              <th className="px-4 py-3 uppercase text-xs"></th>
            </tr>
          </thead>
          <tbody>
            {(admins || []).map((a) => (
              <tr key={a.id} className="border-b border-[#dcd3c6] last:border-b-0" data-testid={`team-row-${a.id}`}>
                <td className="px-4 py-3">
                  {a.name || "—"}
                  {a.id === user?.id && <span className="text-xs text-black ml-2">(you)</span>}
                </td>
                <td className="px-4 py-3">{a.email}</td>
                <td className="px-4 py-3 text-xs">
                  {a.last_login_at ? new Date(a.last_login_at).toLocaleString() : "—"}
                </td>
                <td className="px-4 py-3">
                  {a.status === "pending" ? (
                    <span className="text-xs px-3 py-1 border border-[#c19872] rounded-full" data-testid={`status-pending-${a.id}`}>Invite pending</span>
                  ) : (
                    <span className="text-xs px-3 py-1 bg-[#e2d3c0] rounded-full" data-testid={`status-active-${a.id}`}>Active</span>
                  )}
                </td>
                <td className="px-4 py-3 text-right">
                  {a.status === "pending" && (
                    <button onClick={() => resend(a)} data-testid={`resend-${a.id}`} className="text-xs underline mr-4">
                      <RefreshCw size={11} className="inline mr-1" />Resend
                    </button>
                  )}
                  {a.id !== user?.id && (
                    <button onClick={() => remove(a)} data-testid={`remove-${a.id}`} className="text-xs underline text-[#b35c5c]">
                      <Trash2 size={11} className="inline mr-1" />{a.status === "pending" ? "Cancel invite" : "Remove"}
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {admins && admins.length === 0 && (
              <tr><td colSpan="5" className="px-4 py-8 text-center text-sm">No admins found.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {showInvite && (
        <div className="fixed inset-0 bg-black/30 z-40 flex items-center justify-center px-4" data-testid="invite-modal">
          <form onSubmit={invite} className="bg-white border border-[#c19872] rounded-xl p-8 w-full max-w-md">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-bold">Invite admin</h2>
              <button type="button" onClick={() => setShowInvite(false)} data-testid="invite-cancel"><X size={16} /></button>
            </div>
            <label className="text-xs font-bold block mb-1">Name</label>
            <input data-testid="invite-name" className="btw-input mb-5" value={invName}
                   onChange={(e) => setInvName(e.target.value)} required />
            <label className="text-xs font-bold block mb-1">Email</label>
            <input data-testid="invite-email" type="email" className="btw-input mb-6" value={invEmail}
                   onChange={(e) => setInvEmail(e.target.value)} required />
            <p className="text-xs text-black mb-6">
              They'll get an email with a link to set their password (valid 48 hours, single use).
            </p>
            <button type="submit" disabled={busy} data-testid="invite-submit" className="btw-btn w-full">
              {busy ? "Sending…" : "Send invite"}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
